import { useState, useEffect, useCallback, useRef } from 'react';
import { SENSOR_TYPES, WAFFLE_TYPES } from '../utils/constants';
import useUndoHistory from './useUndoHistory';

/**
 * Custom hook for managing spaces (boxes, shapes, and entry points)
 * with improved state management and robust delete functionality
 * 
 * @param {Object} initialData - Optional initial data to load
 * @returns {Object} Space management functions and state
 */
export const useSpaceManagement = (initialData = null) => {
  const [boxes, setBoxes] = useState(initialData?.boxes || []);
  const [shapes, setShapes] = useState(initialData?.shapes || []);
  const [entryPoints, setEntryPoints] = useState(initialData?.entryPoints || []);
  const [nextId, setNextId] = useState(1);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [selectedSpaces, setSelectedSpaces] = useState([]);
  const [isMultiSelecting, setIsMultiSelecting] = useState(false);
  const [typeVisibility, setTypeVisibility] = useState({
    [SENSOR_TYPES.OA]: true,
    [SENSOR_TYPES.WAFFLE]: true,
    [SENSOR_TYPES.ENTRY]: true
  });
  
  // Reference to track if we've already performed the initial renumbering
  const initialRenumberingDoneRef = useRef(false);
  
  // Initialize undo history
  const { history, addToHistory, undo, clearHistory } = useUndoHistory();

  // Conversion constants
  const METERS_TO_FEET = 3.28084;
  const FEET_TO_METERS = 0.3048;
  
  /**
   * Format area based on current unit with thousands separators
   * @param {number} squareFeet - Area in square feet
   * @returns {string} Formatted area string
   */
  const formatArea = useCallback((squareFeet) => {
    // Round up to nearest whole square foot
    const roundedArea = Math.ceil(squareFeet);
    
    // Format with commas for thousands
    return new Intl.NumberFormat().format(roundedArea);
  }, []);
  
  /**
   * Calculate area for a box
   * @param {Object} box - Box object with coordinates
   * @param {number} scale - Current scale (pixels per foot)
   * @returns {number} Area in square feet
   */
  const calculateBoxArea = useCallback((box, scale) => {
    const { x, y, width, height } = box;
    
    // Validate inputs to prevent NaN
    if (!scale || scale <= 0) {
      console.warn('Invalid scale in calculateBoxArea:', scale);
      scale = 30; // Use default scale as fallback (30px = 1ft)
    }
    
    if (width === undefined || height === undefined) {
      console.warn('Missing dimensions in box:', box);
      return 0; // Return 0 for invalid boxes
    }
    
    // Convert from pixels to feet
    const widthInFeet = width / scale;
    const heightInFeet = height / scale;
    
    // Calculate area in square feet (with safety check)
    const area = widthInFeet * heightInFeet;
    return isNaN(area) ? 0 : area;
  }, []);
  
  /**
   * Calculate area for a polygon
   * @param {Array} vertices - Array of {x, y} points
   * @param {number} scale - Current scale (pixels per foot)
   * @returns {number} Area in square feet
   */
  const calculatePolygonArea = useCallback((vertices, scale) => {
    // Validate inputs to prevent NaN
    if (!vertices || vertices.length < 3) {
      return 0;
    }
    
    if (!scale || scale <= 0) {
      console.warn('Invalid scale in calculatePolygonArea:', scale);
      scale = 30; // Use default scale as fallback (30px = 1ft)
    }
    
    let area = 0;
    const n = vertices.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      
      // Check for valid vertices
      if (!vertices[i] || !vertices[j] || 
          vertices[i].x === undefined || vertices[i].y === undefined || 
          vertices[j].x === undefined || vertices[j].y === undefined) {
        console.warn('Invalid vertex found:', vertices[i], vertices[j]);
        return 0;
      }
      
      // Shoelace formula for polygon area
      area += vertices[i].x * vertices[j].y;
      area -= vertices[j].x * vertices[i].y;
    }
    
    area = Math.abs(area) / 2;
    
    // Convert from square pixels to square feet (with safety check)
    const areaInSqFeet = area / (scale * scale);
    return isNaN(areaInSqFeet) ? 0 : areaInSqFeet;
  }, []);

  /**
   * Helper function to handle renumbering spaces and updating IDs
   * @param {Array} boxArray - Array of boxes
   * @param {Array} shapeArray - Array of shapes  
   * @param {Array} entryArray - Array of entry points
   * @param {number} scale - Current scale in pixels per meter
   * @returns {Object} Object containing renumbered arrays and new next ID
   */
  const renumberSpacesHelper = (boxArray, shapeArray, entryArray, scale = 100) => {
    // Collect all spaces with their types for renumbering
    const allSpaces = [
      ...boxArray.map(box => ({ 
        ...box, 
        spaceType: 'box',
        // Ensure area calculation
        area: box.area || calculateBoxArea(box, scale)
      })),
      ...shapeArray.map(shape => ({ 
        ...shape, 
        spaceType: 'shape',
        // Ensure area calculation
        area: shape.area || calculatePolygonArea(shape.vertices, scale)
      })),
      ...entryArray.map(entry => ({ ...entry, spaceType: 'entry' }))
    ];
    
    // Sort spaces by their current ID
    allSpaces.sort((a, b) => a.id - b.id);
    
    // Create arrays with renumbered IDs
    const renumberedBoxes = [];
    const renumberedShapes = [];
    const renumberedEntryPoints = [];
    let newNextId = 1;
    
    // Assign new IDs
    allSpaces.forEach(space => {
      if (!space) {
        console.warn('Encountered undefined space during renumbering');
        return; // Skip this iteration
      }
      
      const { spaceType, ...restOfSpace } = space;
      const spaceWithNewId = { ...restOfSpace, id: newNextId };
      newNextId++;
      
      // Add to the appropriate array
      if (spaceType === 'box') {
        renumberedBoxes.push(spaceWithNewId);
      } else if (spaceType === 'shape') {
        renumberedShapes.push(spaceWithNewId);
      } else if (spaceType === 'entry') {
        renumberedEntryPoints.push(spaceWithNewId);
      }
    });
    
    return {
      boxes: renumberedBoxes,
      shapes: renumberedShapes,
      entryPoints: renumberedEntryPoints,
      nextId: newNextId,
      allSpaces // Return this for selection mapping
    };
  };

  /**
   * Renumber spaces to maintain a continuous sequence of IDs
   * This ensures that if space #3 is deleted, space #4 becomes #3, and so on
   * Also recalculates areas for all spaces as if they were drawn anew
   * @param {number} scale - Current scale (pixels per meter)
   */
  const renumberSpaces = useCallback((scale) => {
    // Use default scale if not provided
    const currentScale = scale || 100; // 100 pixels per meter as default
    
    // Safety check - if all collections are empty, just update nextId and return
    if (boxes.length === 0 && shapes.length === 0 && entryPoints.length === 0) {
      setNextId(1);
      return;
    }
    
    // Use our helper to do the renumbering
    const result = renumberSpacesHelper(boxes, shapes, entryPoints, currentScale);
    
    // Update selection references if needed
    let newSelectedSpace = selectedSpace;
    if (selectedSpace) {
      // Find the space in the sorted list
      const index = result.allSpaces.findIndex(space => 
        space.spaceType === selectedSpace.type && space.id === selectedSpace.id
      );
      
      if (index !== -1) {
        // Get the new ID for this space
        const newId = index + 1;
        newSelectedSpace = { ...selectedSpace, id: newId };
      }
    }
    
    // Update multi-selection references
    const newSelectedSpaces = selectedSpaces.map(space => {
      const index = result.allSpaces.findIndex(s => 
        s.spaceType === space.type && s.id === space.id
      );
      
      if (index !== -1) {
        const newId = index + 1;
        return { ...space, id: newId };
      }
      
      return space;
    });
    
    // Update the state
    setBoxes(result.boxes);
    setShapes(result.shapes);
    setEntryPoints(result.entryPoints);
    setNextId(result.nextId);
    setSelectedSpace(newSelectedSpace);
    setSelectedSpaces(newSelectedSpaces);
  }, [boxes, shapes, entryPoints, selectedSpace, selectedSpaces]);
  
  // Validate and renumber IDs when initialData is loaded
  useEffect(() => {
    // Only run this once when initialData is first available
    if (initialData && 
        !initialRenumberingDoneRef.current && 
        (initialData.boxes?.length > 0 || initialData.shapes?.length > 0 || initialData.entry_points?.length > 0 || initialData.entryPoints?.length > 0)) {
      
      console.log('Validating and renumbering space IDs on initial load');
      
      // Mark that we've done the initial renumbering to prevent infinite loops
      initialRenumberingDoneRef.current = true;
      
      // Use a short delay to ensure all state has been initialized
      setTimeout(() => {
        renumberSpaces();
      }, 0);
    }
  }, [initialData, renumberSpaces]);

  /**
   * Delete a space with functional updates
   * @param {string} spaceType - Type of space ('box', 'shape', or 'entry')
   * @param {number} spaceId - ID of the space to delete
   * @param {number} scale - Current scale (pixels per meter)
   */
  const deleteSpace = useCallback((spaceType, spaceId, scale) => {
    try {
      // Use default scale if not provided
      const currentScale = scale || 100; // 100 pixels per meter as default
      
      // Store the space for undo (before deletion)
      let spaceToDelete = null;
      
      // Create new arrays without the deleted space
      let newBoxes = [...boxes];
      let newShapes = [...shapes];
      let newEntryPoints = [...entryPoints];
      
      if (spaceType === 'box') {
        spaceToDelete = boxes.find(box => box.id === spaceId);
        if (spaceToDelete) {
          // Add to history before deleting
          addToHistory({
            type: 'DELETE_BOX',
            data: { box: spaceToDelete }
          });
          
          // Remove the box from our copied array
          newBoxes = newBoxes.filter(box => box.id !== spaceId);
        } else {
          console.warn(`No box found with ID ${spaceId}`);
          return false; // Exit early if nothing to delete
        }
      } else if (spaceType === 'shape') {
        spaceToDelete = shapes.find(shape => shape.id === spaceId);
        if (spaceToDelete) {
          // Add to history before deleting
          addToHistory({
            type: 'DELETE_SHAPE',
            data: { shape: spaceToDelete }
          });
          
          // Remove the shape from our copied array
          newShapes = newShapes.filter(shape => shape.id !== spaceId);
        } else {
          console.warn(`No shape found with ID ${spaceId}`);
          return false; // Exit early if nothing to delete
        }
      } else if (spaceType === 'entry') {
        spaceToDelete = entryPoints.find(entry => entry.id === spaceId);
        if (spaceToDelete) {
          // Add to history before deleting
          addToHistory({
            type: 'DELETE_ENTRY',
            data: { entry: spaceToDelete }
          });
          
          // Remove the entry point from our copied array
          newEntryPoints = newEntryPoints.filter(entry => entry.id !== spaceId);
        } else {
          console.warn(`No entry point found with ID ${spaceId}`);
          return false; // Exit early if nothing to delete
        }
      } else {
        console.error(`Invalid space type: ${spaceType}`);
        return false; // Exit early if invalid type
      }
      
      // Use our helper to renumber spaces
      const result = renumberSpacesHelper(newBoxes, newShapes, newEntryPoints, currentScale);
      
      // Update selection references
      let newSelectedSpace = selectedSpace;
      if (selectedSpace && selectedSpace.type === spaceType && selectedSpace.id === spaceId) {
        newSelectedSpace = null;
      } else if (selectedSpace) {
        // Find the new ID for the selected space
        const index = result.allSpaces.findIndex(space => 
          space.spaceType === selectedSpace.type && space.id === selectedSpace.id
        );
        
        if (index !== -1) {
          const newId = index + 1; // New IDs start at 1
          newSelectedSpace = { ...selectedSpace, id: newId };
        }
      }
      
      // Update multi-selection references
      let newSelectedSpaces = selectedSpaces.filter(
        space => !(space.type === spaceType && space.id === spaceId)
      );
      
      // Update IDs in multi-selection
      if (newSelectedSpaces.length > 0) {
        newSelectedSpaces = newSelectedSpaces.map(selSpace => {
          const index = result.allSpaces.findIndex(s => 
            s.spaceType === selSpace.type && s.id === selSpace.id
          );
          
          if (index !== -1) {
            const newId = index + 1; // New IDs start at 1
            return { ...selSpace, id: newId };
          }
          
          return selSpace;
        });
      }
      
      // Update all states at once
      setBoxes(result.boxes);
      setShapes(result.shapes);
      setEntryPoints(result.entryPoints);
      setNextId(result.nextId);
      setSelectedSpace(newSelectedSpace);
      setSelectedSpaces(newSelectedSpaces);
      
      return true;
    } catch (error) {
      console.error('Error deleting space:', error);
      return false;
    }
  }, [boxes, shapes, entryPoints, addToHistory, selectedSpace, selectedSpaces]);

  /**
   * Clear all spaces
   */
  const clearAllSpaces = useCallback(() => {
    // Store current state for undo
    if (boxes.length > 0 || shapes.length > 0 || entryPoints.length > 0) {
      addToHistory({
        type: 'CLEAR_ALL',
        data: {
          boxes: [...boxes],
          shapes: [...shapes],
          entryPoints: [...entryPoints],
          nextId
        }
      });
    }
    
    setBoxes([]);
    setShapes([]);
    setEntryPoints([]);
    setNextId(1);
    setSelectedSpace(null);
    setSelectedSpaces([]);
  }, [boxes, shapes, entryPoints, nextId, addToHistory]);

  /**
   * Toggle space type between OA and Waffle
   * @param {string} spaceType - Type of space ('box', 'shape', or 'entry')
   * @param {number} spaceId - ID of the space
   */
  const toggleSpaceType = useCallback((spaceType, spaceId) => {
    if (spaceType === 'box') {
      const box = boxes.find(b => b.id === spaceId);
      if (box) {
        // Store for undo
        addToHistory({
          type: 'TOGGLE_BOX_TYPE',
          data: { box: { ...box } }
        });
        
        setBoxes(prevBoxes => prevBoxes.map(box => {
          if (box.id === spaceId) {
            // Only allow toggling between OA and Waffle (not Entry)
            let newType;
            if (box.type === SENSOR_TYPES.OA) {
              newType = SENSOR_TYPES.WAFFLE;
            } else if (box.type === SENSOR_TYPES.WAFFLE) {
              newType = SENSOR_TYPES.OA;
            } else {
              return box; // Don't change Entry type
            }
            
            // If switching to OA, use a default count of 1
            // We're not calculating based on ceiling height anymore
            let newCount = 1;
            
            return { 
              ...box, 
              type: newType,
              count: newCount
            };
          }
          return box;
        }));
      }
    } else if (spaceType === 'shape') {
      const shape = shapes.find(s => s.id === spaceId);
      if (shape) {
        // Store for undo
        addToHistory({
          type: 'TOGGLE_SHAPE_TYPE',
          data: { shape: { ...shape } }
        });
        
        setShapes(prevShapes => prevShapes.map(shape => {
          if (shape.id === spaceId) {
            // Only allow toggling between OA and Waffle (not Entry)
            let newType;
            if (shape.type === SENSOR_TYPES.OA) {
              newType = SENSOR_TYPES.WAFFLE;
            } else if (shape.type === SENSOR_TYPES.WAFFLE) {
              newType = SENSOR_TYPES.OA;
            } else {
              return shape; // Don't change Entry type
            }
            
            // If switching to OA, use a default count of 1
            // We're not calculating based on ceiling height anymore
            let newCount = 1;
            
            return { 
              ...shape, 
              type: newType,
              count: newCount
            };
          }
          return shape;
        }));
      }
    }
    // Entry points can't change type
  }, [boxes, shapes, addToHistory]);

  /**
   * Update space count (OA, Waffle, or Entry)
   * @param {string} spaceType - Type of space ('box', 'shape', or 'entry')
   * @param {number} spaceId - ID of the space
   * @param {number} newCount - New count value
   */
  const updateSpaceCount = useCallback((spaceType, spaceId, newCount) => {
    if (newCount < 1) return; // Don't allow counts less than 1
    
    if (spaceType === 'box') {
      const box = boxes.find(b => b.id === spaceId);
      if (box) {
        // Store for undo
        addToHistory({
          type: 'UPDATE_BOX_COUNT',
          data: { box: { ...box } }
        });
        
        setBoxes(prevBoxes => prevBoxes.map(box => {
          if (box.id === spaceId) {
            return { ...box, count: newCount };
          }
          return box;
        }));
      }
    } else if (spaceType === 'shape') {
      const shape = shapes.find(s => s.id === spaceId);
      if (shape) {
        // Store for undo
        addToHistory({
          type: 'UPDATE_SHAPE_COUNT',
          data: { shape: { ...shape } }
        });
        
        setShapes(prevShapes => prevShapes.map(shape => {
          if (shape.id === spaceId) {
            return { ...shape, count: newCount };
          }
          return shape;
        }));
      }
    } else if (spaceType === 'entry') {
      const entry = entryPoints.find(e => e.id === spaceId);
      if (entry) {
        // Store for undo
        addToHistory({
          type: 'UPDATE_ENTRY_COUNT',
          data: { entry: { ...entry } }
        });
        
        setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => {
          if (entry.id === spaceId) {
            return { ...entry, count: newCount };
          }
          return entry;
        }));
      }
    }
  }, [boxes, shapes, entryPoints, addToHistory]);

  /**
   * Toggle visibility of a space
   * @param {string} spaceType - Type of space ('box', 'shape', or 'entry')
   * @param {number} spaceId - ID of the space
   */
  const toggleSpaceVisibility = useCallback((spaceType, spaceId) => {
    if (spaceType === 'box') {
      const box = boxes.find(b => b.id === spaceId);
      if (box) {
        // Store for undo
        addToHistory({
          type: 'TOGGLE_BOX_VISIBILITY',
          data: { box: { ...box } }
        });
        
        setBoxes(prevBoxes => prevBoxes.map(box => 
          box.id === spaceId ? { ...box, visible: !box.visible } : box
        ));
      }
    } else if (spaceType === 'shape') {
      const shape = shapes.find(s => s.id === spaceId);
      if (shape) {
        // Store for undo
        addToHistory({
          type: 'TOGGLE_SHAPE_VISIBILITY',
          data: { shape: { ...shape } }
        });
        
        setShapes(prevShapes => prevShapes.map(shape => 
          shape.id === spaceId ? { ...shape, visible: !shape.visible } : shape
        ));
      }
    } else if (spaceType === 'entry') {
      const entry = entryPoints.find(e => e.id === spaceId);
      if (entry) {
        // Store for undo
        addToHistory({
          type: 'TOGGLE_ENTRY_VISIBILITY',
          data: { entry: { ...entry } }
        });
        
        setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => 
          entry.id === spaceId ? { ...entry, visible: !entry.visible } : entry
        ));
      }
    }
  }, [boxes, shapes, entryPoints, addToHistory]);

  /**
   * Toggle visibility of all spaces of a specific type
   * @param {string} type - Sensor type (OA, WAFFLE, ENTRY)
   */
  const toggleTypeVisibility = useCallback((type) => {
    setTypeVisibility(prevVisibility => ({
      ...prevVisibility,
      [type]: !prevVisibility[type]
    }));
    
    // Update visibility of all spaces of this type
    setBoxes(prevBoxes => prevBoxes.map(box => 
      box.type === type ? { ...box, visible: !typeVisibility[type] } : box
    ));
    
    setShapes(prevShapes => prevShapes.map(shape => 
      shape.type === type ? { ...shape, visible: !typeVisibility[type] } : shape
    ));
    
    setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => 
      entry.type === type ? { ...entry, visible: !typeVisibility[type] } : entry
    ));
  }, [typeVisibility]);

  /**
   * Toggle visibility of selected spaces
   * @param {boolean} visible - Whether to make spaces visible or hidden
   */
  const toggleSelectedSpacesVisibility = useCallback((visible) => {
    // Update boxes
    setBoxes(prevBoxes => prevBoxes.map(box => {
      const isSelected = selectedSpaces.some(
        space => space.type === 'box' && space.id === box.id
      );
      return isSelected ? { ...box, visible } : box;
    }));
    
    // Update shapes
    setShapes(prevShapes => prevShapes.map(shape => {
      const isSelected = selectedSpaces.some(
        space => space.type === 'shape' && space.id === shape.id
      );
      return isSelected ? { ...shape, visible } : shape;
    }));
    
    // Update entry points
    setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => {
      const isSelected = selectedSpaces.some(
        space => space.type === 'entry' && space.id === entry.id
      );
      return isSelected ? { ...entry, visible } : entry;
    }));
    
    // Clear selection after toggling
    setSelectedSpaces([]);
    setIsMultiSelecting(false);
  }, [selectedSpaces]);

  /**
   * Handle command-click selection
   * @param {Event} event - Mouse event
   * @param {string} spaceType - Type of space ('box', 'shape', or 'entry')
   * @param {number} spaceId - ID of the space
   */
  const handleCommandClick = useCallback((event, spaceType, spaceId) => {
    event.stopPropagation();
    console.log(`[COMMAND CLICK] Called with ${spaceType} ID: ${spaceId}`);
    console.log(`  Current selectedSpaces:`, selectedSpaces);
    console.log(`  Current selectedSpace:`, selectedSpace);
    
    setIsMultiSelecting(true);
    
    // If this is the first multi-select, add the currently selected space to selectedSpaces
    if (selectedSpaces.length === 0 && selectedSpace) {
      console.log(`  First multi-select, adding current selection to array:`, selectedSpace);
      setSelectedSpaces([selectedSpace]);
    }
    
    // Check if space is already selected
    const isAlreadySelected = selectedSpaces.some(
      space => space.type === spaceType && space.id === spaceId
    );
    
    console.log(`  Is space already selected? ${isAlreadySelected}`);
    
    if (isAlreadySelected) {
      // Remove from selection
      console.log(`  Removing space from selection`);
      setSelectedSpaces(prevSelectedSpaces => {
        const filtered = prevSelectedSpaces.filter(
          space => !(space.type === spaceType && space.id === spaceId)
        );
        console.log(`  New selectedSpaces after removal:`, filtered);
        return filtered;
      });
    } else {
      // Add to selection
      console.log(`  Adding space to selection`);
      setSelectedSpaces(prevSelectedSpaces => {
        const newArray = [...prevSelectedSpaces, { type: spaceType, id: spaceId }];
        console.log(`  New selectedSpaces after addition:`, newArray);
        return newArray;
      });
    }
    
    // Clear the single selection when in multi-select mode
    setSelectedSpace(null);
    console.log(`  Cleared single selection`);
  }, [selectedSpaces, selectedSpace]);

  /**
   * Handle single-click selection
   * @param {string} spaceType - Type of space ('box', 'shape', or 'entry')
   * @param {number} spaceId - ID of the space
   */
  const handleSingleClick = useCallback((spaceType, spaceId) => {
    setSelectedSpace({ type: spaceType, id: spaceId });
    // Clear multi-selection when single selecting
    setSelectedSpaces([]);
    setIsMultiSelecting(false);
  }, []);

  /**
   * Calculate total area of all spaces - just sum the areas without multiplying by count
   * @returns {number} Total area in square meters
   */
  const calculateTotalArea = useCallback(() => {
    let totalArea = 0;
    
    // Add box areas (just sum the areas)
    boxes.forEach(box => {
      totalArea += box.area;
    });
    
    // Add shape areas (just sum the areas)
    shapes.forEach(shape => {
      totalArea += shape.area;
    });
    
    return totalArea;
  }, [boxes, shapes]);

  /**
   * Calculate total count of each sensor type
   * @returns {Object} Object with counts for each sensor type
   */
  const calculateSensorCounts = useCallback(() => {
    let oaCount = 0;
    let waffleCount = 0;
    let entryCount = 0;
    
    // Count boxes
    boxes.forEach(box => {
      if (box.type === SENSOR_TYPES.OA) {
        oaCount += box.count || 1;
      } else if (box.type === SENSOR_TYPES.WAFFLE) {
        waffleCount += box.count || 1;
      } else if (box.type === SENSOR_TYPES.ENTRY) {
        entryCount += box.count || 1;
      }
    });
    
    // Count shapes
    shapes.forEach(shape => {
      if (shape.type === SENSOR_TYPES.OA) {
        oaCount += shape.count || 1;
      } else if (shape.type === SENSOR_TYPES.WAFFLE) {
        waffleCount += shape.count || 1;
      } else if (shape.type === SENSOR_TYPES.ENTRY) {
        entryCount += shape.count || 1;
      }
    });
    
    // Count entry points
    entryPoints.forEach(entry => {
      if (entry.type === SENSOR_TYPES.OA) {
        oaCount += entry.count || 1;
      } else if (entry.type === SENSOR_TYPES.WAFFLE) {
        waffleCount += entry.count || 1;
      } else if (entry.type === SENSOR_TYPES.ENTRY) {
        entryCount += entry.count || 1;
      }
    });
    
    return { oaCount, waffleCount, entryCount };
  }, [boxes, shapes, entryPoints]);

  /**
   * Add a box to the spaces
   * @param {Object} box - Box object to add
   * @returns {Object|null} The new box object or null if too small
   */
  const addBox = useCallback((box) => {
    // Check if the box is too small (less than 2 square feet)
    const squareFeet = box.area * METERS_TO_FEET * METERS_TO_FEET;
    if (squareFeet < 2) {
      console.log('Box too small:', squareFeet.toFixed(2), 'sqft. Minimum size is 2 sqft.');
      return null; // Don't add boxes smaller than 2 square feet
    }
    
    // Ensure ID uniqueness by checking all existing IDs
    const existingIds = new Set([
      ...boxes.map(box => box.id),
      ...shapes.map(shape => shape.id),
      ...entryPoints.map(entry => entry.id)
    ]);
    
    // Find next available ID by incrementing until finding unused ID
    let safeNextId = nextId;
    while (existingIds.has(safeNextId)) {
      safeNextId++;
    }
    
    const newBox = { ...box, id: safeNextId };
    
    // Add to history
    addToHistory({
      type: 'ADD_BOX',
      data: { boxId: safeNextId }
    });
    
    setBoxes(prevBoxes => [...prevBoxes, newBox]);
    setNextId(safeNextId + 1);
    
    return newBox;
  }, [nextId, addToHistory, METERS_TO_FEET, boxes, shapes, entryPoints]);

  /**
   * Add a shape to the spaces
   * @param {Object} shape - Shape object to add
   * @returns {Object|null} The new shape object or null if too small
   */
  const addShape = useCallback((shape) => {
    // Check if the shape is too small (less than 2 square feet)
    const squareFeet = shape.area * METERS_TO_FEET * METERS_TO_FEET;
    if (squareFeet < 2) {
      console.log('Shape too small:', squareFeet.toFixed(2), 'sqft. Minimum size is 2 sqft.');
      return null; // Don't add shapes smaller than 2 square feet
    }
    
    // Ensure ID uniqueness by checking all existing IDs
    const existingIds = new Set([
      ...boxes.map(box => box.id),
      ...shapes.map(shape => shape.id),
      ...entryPoints.map(entry => entry.id)
    ]);
    
    // Find next available ID by incrementing until finding unused ID
    let safeNextId = nextId;
    while (existingIds.has(safeNextId)) {
      safeNextId++;
    }
    
    const newShape = { ...shape, id: safeNextId };
    
    // Add to history
    addToHistory({
      type: 'ADD_SHAPE',
      data: { shapeId: safeNextId }
    });
    
    setShapes(prevShapes => [...prevShapes, newShape]);
    setNextId(safeNextId + 1);
    
    return newShape;
  }, [nextId, addToHistory, METERS_TO_FEET, boxes, shapes, entryPoints]);

  /**
   * Add an entry point to the spaces
   * @param {Object} entry - Entry point object to add
   */
  const addEntryPoint = useCallback((entry) => {
    // Ensure ID uniqueness by checking all existing IDs
    const existingIds = new Set([
      ...boxes.map(box => box.id),
      ...shapes.map(shape => shape.id),
      ...entryPoints.map(entry => entry.id)
    ]);
    
    // Find next available ID by incrementing until finding unused ID
    let safeNextId = nextId;
    while (existingIds.has(safeNextId)) {
      safeNextId++;
    }
    
    const newEntry = { ...entry, id: safeNextId };
    
    // Add to history
    addToHistory({
      type: 'ADD_ENTRY',
      data: { entryId: safeNextId }
    });
    
    setEntryPoints(prevEntryPoints => [...prevEntryPoints, newEntry]);
    setNextId(safeNextId + 1);
    
    return newEntry;
  }, [nextId, addToHistory, boxes, shapes, entryPoints]);

  /**
   * Handle undo action
   * @param {number} scale - Current scale (pixels per meter)
   */
  const handleUndo = useCallback((scale) => {
    const action = undo();
    if (!action) return;
    
    console.log('Undoing action:', action);
    
    switch (action.type) {
      case 'ADD_BOX':
        setBoxes(prevBoxes => prevBoxes.filter(box => box.id !== action.data.boxId));
        break;
        
      case 'ADD_SHAPE':
        setShapes(prevShapes => prevShapes.filter(shape => shape.id !== action.data.shapeId));
        break;
        
      case 'ADD_ENTRY':
        setEntryPoints(prevEntryPoints => prevEntryPoints.filter(entry => entry.id !== action.data.entryId));
        break;
        
      case 'DELETE_BOX':
        setBoxes(prevBoxes => [...prevBoxes, action.data.box]);
        break;
        
      case 'DELETE_SHAPE':
        setShapes(prevShapes => [...prevShapes, action.data.shape]);
        break;
        
      case 'DELETE_ENTRY':
        setEntryPoints(prevEntryPoints => [...prevEntryPoints, action.data.entry]);
        break;
        
      case 'TOGGLE_BOX_TYPE':
        setBoxes(prevBoxes => prevBoxes.map(box => 
          box.id === action.data.box.id ? action.data.box : box
        ));
        break;
        
      case 'TOGGLE_SHAPE_TYPE':
        setShapes(prevShapes => prevShapes.map(shape => 
          shape.id === action.data.shape.id ? action.data.shape : shape
        ));
        break;
        
      case 'UPDATE_BOX_COUNT':
        setBoxes(prevBoxes => prevBoxes.map(box => 
          box.id === action.data.box.id ? action.data.box : box
        ));
        break;
        
      case 'UPDATE_SHAPE_COUNT':
        setShapes(prevShapes => prevShapes.map(shape => 
          shape.id === action.data.shape.id ? action.data.shape : shape
        ));
        break;
        
      case 'UPDATE_ENTRY_COUNT':
        setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => 
          entry.id === action.data.entry.id ? action.data.entry : entry
        ));
        break;
        
      case 'TOGGLE_BOX_VISIBILITY':
        setBoxes(prevBoxes => prevBoxes.map(box => 
          box.id === action.data.box.id ? action.data.box : box
        ));
        break;
        
      case 'TOGGLE_SHAPE_VISIBILITY':
        setShapes(prevShapes => prevShapes.map(shape => 
          shape.id === action.data.shape.id ? action.data.shape : shape
        ));
        break;
        
      case 'TOGGLE_ENTRY_VISIBILITY':
        setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => 
          entry.id === action.data.entry.id ? action.data.entry : entry
        ));
        break;
        
      case 'UPDATE_BOX_ATTRIBUTE':
        setBoxes(prevBoxes => prevBoxes.map(box => {
          if (box.id === action.data.box.id) {
            const restoredBox = { ...box };
            restoredBox[action.data.attributeName] = action.data.oldValue;
            return restoredBox;
          }
          return box;
        }));
        break;
        
      case 'UPDATE_SHAPE_ATTRIBUTE':
        setShapes(prevShapes => prevShapes.map(shape => {
          if (shape.id === action.data.shape.id) {
            const restoredShape = { ...shape };
            restoredShape[action.data.attributeName] = action.data.oldValue;
            return restoredShape;
          }
          return shape;
        }));
        break;
        
      case 'UPDATE_ENTRY_ATTRIBUTE':
        setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => {
          if (entry.id === action.data.entry.id) {
            const restoredEntry = { ...entry };
            restoredEntry[action.data.attributeName] = action.data.oldValue;
            return restoredEntry;
          }
          return entry;
        }));
        break;
        
      case 'CLEAR_ALL':
        setBoxes(action.data.boxes);
        setShapes(action.data.shapes);
        setEntryPoints(action.data.entryPoints);
        setNextId(action.data.nextId);
        break;
        
      default:
        console.warn('Unknown action type:', action.type);
    }
    
    // For actions that may have affected the ID sequence,
    // schedule renumbering after state updates
    if (['ADD_BOX', 'ADD_SHAPE', 'ADD_ENTRY', 'DELETE_BOX', 'DELETE_SHAPE', 'DELETE_ENTRY', 'CLEAR_ALL'].includes(action.type)) {
      // Use a function that triggers renumbering but doesn't directly reference the renumberSpaces function
      // to avoid circular dependencies
      setTimeout(() => {
        console.log('Renumbering spaces after undo operation');
        // Use default scale if not provided
        const currentScale = scale || 100; // 100 pixels per meter as default
        
        // Implementation of renumbering logic here to avoid circular references
        // This is a simplified version of renumberSpaces
        // Collect all spaces with their types
        const allSpaces = [
          ...boxes.map(box => ({ 
            ...box, 
            spaceType: 'box',
            // Recalculate area in square meters
            area: calculateBoxArea(box, currentScale)
          })),
          ...shapes.map(shape => ({ 
            ...shape, 
            spaceType: 'shape',
            // Recalculate area for polygon in square meters
            area: calculatePolygonArea(shape.vertices, currentScale)
          })),
          ...entryPoints.map(entry => ({ ...entry, spaceType: 'entry' }))
        ];
        
        // Sort spaces by their current ID
        allSpaces.sort((a, b) => a.id - b.id);
        
        // Create new arrays with renumbered IDs
        const newBoxes = [];
        const newShapes = [];
        const newEntryPoints = [];
        let newNextId = 1;
        
        // Assign new IDs
        allSpaces.forEach(space => {
          const { spaceType, ...restOfSpace } = space;
          const spaceWithNewId = { ...restOfSpace, id: newNextId };
          newNextId++;
          
          // Add to the appropriate array
          if (spaceType === 'box') {
            newBoxes.push(spaceWithNewId);
          } else if (spaceType === 'shape') {
            newShapes.push(spaceWithNewId);
          } else if (spaceType === 'entry') {
            newEntryPoints.push(spaceWithNewId);
          }
        });
        
        // Update selection references if needed
        let newSelectedSpace = selectedSpace;
        if (selectedSpace) {
          // Find the space in the sorted list
          const index = allSpaces.findIndex(space => 
            space.spaceType === selectedSpace.type && space.id === selectedSpace.id
          );
          
          if (index !== -1) {
            // Get the new ID for this space
            const newId = index + 1;
            newSelectedSpace = { ...selectedSpace, id: newId };
          }
        }
        
        // Update multi-selection references
        const newSelectedSpaces = selectedSpaces.map(space => {
          const index = allSpaces.findIndex(s => 
            s.spaceType === space.type && s.id === space.id
          );
          
          if (index !== -1) {
            const newId = index + 1;
            return { ...space, id: newId };
          }
          
          return space;
        });
        
        // Update the state
        setBoxes(newBoxes);
        setShapes(newShapes);
        setEntryPoints(newEntryPoints);
        setNextId(newNextId);
        setSelectedSpace(newSelectedSpace);
        setSelectedSpaces(newSelectedSpaces);
        
        console.log('Spaces renumbered and areas recalculated successfully after undo');
      }, 0);
    }
  }, [boxes, shapes, entryPoints, undo, selectedSpace, selectedSpaces, calculateBoxArea, calculatePolygonArea]);

  // Add keyboard listener for undo (z key)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Check for z key (not cmd+z to keep it simple)
      if (e.key === 'z' && !e.metaKey && !e.ctrlKey) {
        console.log('Z key pressed - Attempting to undo action');
        console.log('Current history state:', history);
        
        // Stop propagation to prevent other handlers from executing
        e.preventDefault();
        e.stopPropagation();
        
        // Execute undo with a default scale of 100 pixels per meter
        handleUndo(100);
      }
    };
    
    // Add as a capture phase listener to ensure it runs first
    document.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleUndo, history]);

  /**
   * Delete multiple selected spaces
   * All spaces in the selectedSpaces array will be deleted
   * @param {number} scale - Current scale (pixels per meter)
   */
  const deleteSelectedSpaces = useCallback((scale) => {
    try {
      console.log(`[DELETE SELECTED SPACES] Deleting ${selectedSpaces.length} spaces`);
      
      if (selectedSpaces.length === 0) {
        console.log(`No spaces selected, nothing to delete`);
        return false;
      }
      
      // Use default scale if not provided
      const currentScale = scale || 100; // 100 pixels per meter as default
      
      // Store in history for undo
      addToHistory({
        type: 'DELETE_MULTIPLE_SPACES',
        data: { 
          boxes: [...boxes], 
          shapes: [...shapes], 
          entryPoints: [...entryPoints], 
          selectedSpaces: [...selectedSpaces] 
        }
      });
      
      // Process deletions for each space type
      const newBoxes = boxes.filter(box => {
        const shouldKeep = !selectedSpaces.some(space => space.type === 'box' && space.id === box.id);
        return shouldKeep;
      });
      
      const newShapes = shapes.filter(shape => {
        const shouldKeep = !selectedSpaces.some(space => space.type === 'shape' && space.id === shape.id);
        return shouldKeep;
      });
      
      const newEntryPoints = entryPoints.filter(entry => {
        const shouldKeep = !selectedSpaces.some(space => space.type === 'entry' && space.id === entry.id);
        return shouldKeep;
      });
      
      // Use our helper to renumber spaces
      const result = renumberSpacesHelper(newBoxes, newShapes, newEntryPoints, currentScale);
      
      // Update all states at once
      setBoxes(result.boxes);
      setShapes(result.shapes);
      setEntryPoints(result.entryPoints);
      setNextId(result.nextId);
      setSelectedSpace(null);
      setSelectedSpaces([]);
      setIsMultiSelecting(false);
      
      console.log(`Deleted ${selectedSpaces.length} selected spaces successfully`);
      return true;
    } catch (error) {
      console.error('Error deleting selected spaces:', error);
      return false;
    }
  }, [boxes, shapes, entryPoints, selectedSpaces, addToHistory]);
  
  /**
   * Update a specific attribute of a space
   * @param {string} spaceType - Type of space ('box', 'shape', or 'entry')
   * @param {number} spaceId - ID of the space
   * @param {string} attributeName - Name of the attribute to update
   * @param {any} value - New value for the attribute
   */
  const updateSpaceAttribute = useCallback((spaceType, spaceId, attributeName, value) => {
    if (spaceType === 'box') {
      const box = boxes.find(b => b.id === spaceId);
      if (box) {
        // Store for undo
        addToHistory({
          type: 'UPDATE_BOX_ATTRIBUTE',
          data: { 
            box: { ...box },
            attributeName,
            oldValue: box[attributeName]
          }
        });
        
        setBoxes(prevBoxes => prevBoxes.map(box => {
          if (box.id === spaceId) {
            return { 
              ...box, 
              [attributeName]: value
            };
          }
          return box;
        }));
      }
    } else if (spaceType === 'shape') {
      const shape = shapes.find(s => s.id === spaceId);
      if (shape) {
        // Store for undo
        addToHistory({
          type: 'UPDATE_SHAPE_ATTRIBUTE',
          data: { 
            shape: { ...shape },
            attributeName,
            oldValue: shape[attributeName]
          }
        });
        
        setShapes(prevShapes => prevShapes.map(shape => {
          if (shape.id === spaceId) {
            return { 
              ...shape, 
              [attributeName]: value
            };
          }
          return shape;
        }));
      }
    } else if (spaceType === 'entry') {
      const entry = entryPoints.find(e => e.id === spaceId);
      if (entry) {
        // Store for undo
        addToHistory({
          type: 'UPDATE_ENTRY_ATTRIBUTE',
          data: { 
            entry: { ...entry },
            attributeName,
            oldValue: entry[attributeName]
          }
        });
        
        setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => {
          if (entry.id === spaceId) {
            return { 
              ...entry, 
              [attributeName]: value
            };
          }
          return entry;
        }));
      }
    }
  }, [boxes, shapes, entryPoints, addToHistory]);

  /**
   * Recalculate all space areas based on current dimensions
   * @param {number} scale - Current scale (pixels per meter)
   */
  const recalculateAllAreas = useCallback((scale) => {
    // Use default scale if not provided
    const currentScale = scale || 100; // 100 pixels per meter as default
    
    console.log('Recalculating all space areas with scale:', currentScale);
    
    // Update boxes with recalculated areas
    setBoxes(prevBoxes => prevBoxes.map(box => ({
      ...box,
      area: calculateBoxArea(box, currentScale)
    })));
    
    // Update shapes with recalculated areas
    setShapes(prevShapes => prevShapes.map(shape => ({
      ...shape,
      area: calculatePolygonArea(shape.vertices, currentScale)
    })));
    
    // Note: Entry points don't have areas to recalculate
    
    console.log('All space areas recalculated successfully');
  }, [calculateBoxArea, calculatePolygonArea]);

  /**
   * Recalculate box dimensions based on container resizing
   * This preserves the real-world dimensions while adjusting pixels
   * @param {number} scale - Current scale in pixels per meter
   * @param {Object} oldDimensions - Previous container dimensions {width, height}
   * @param {Object} newDimensions - New container dimensions {width, height}
   */
  const recalculateBoxDimensions = useCallback((scale, oldDimensions, newDimensions) => {
    if (!scale || !oldDimensions || !newDimensions) {
      console.warn('Missing parameters for recalculateBoxDimensions');
      return;
    }
    
    console.log('Recalculating box dimensions for container resize:', oldDimensions, '->', newDimensions);
    
    // Calculate scale factors (how much the container changed)
    const widthRatio = newDimensions.width / oldDimensions.width;
    const heightRatio = newDimensions.height / oldDimensions.height;
    
    // Only recalculate if there's a significant change (avoid tiny dimension fluctuations)
    if (Math.abs(1 - widthRatio) < 0.01 && Math.abs(1 - heightRatio) < 0.01) {
      console.log('Dimension change too small, skipping recalculation');
      return;
    }
    
    // Create a helper to adjust both box coordinates and size
    const adjustBox = (box) => {
      // If box already has stored real-world dimensions, use those
      // Otherwise, calculate and store them
      const realWorldWidth = box.realWorldWidth || box.width / scale;
      const realWorldHeight = box.realWorldHeight || box.height / scale;
      
      // For coordinates, store real-world positions if not already present
      const realWorldStartX = box.realWorldStartX || box.start.x / scale;
      const realWorldStartY = box.realWorldStartY || box.start.y / scale;
      const realWorldEndX = box.realWorldEndX || box.end.x / scale;
      const realWorldEndY = box.realWorldEndY || box.end.y / scale;
      
      // Calculate new pixel dimensions and position based on container size
      return {
        ...box,
        start: {
          x: realWorldStartX * scale * widthRatio,
          y: realWorldStartY * scale * heightRatio
        },
        end: {
          x: realWorldEndX * scale * widthRatio,
          y: realWorldEndY * scale * heightRatio
        },
        width: realWorldWidth * scale * widthRatio,
        height: realWorldHeight * scale * heightRatio,
        // Store real world dimensions for future reference
        realWorldWidth,
        realWorldHeight,
        realWorldStartX,
        realWorldStartY,
        realWorldEndX,
        realWorldEndY
      };
    };
    
    // Adjust all boxes
    setBoxes(prevBoxes => prevBoxes.map(adjustBox));
    
    // Adjust shapes: store and maintain real-world vertex coordinates
    setShapes(prevShapes => prevShapes.map(shape => {
      // Get or calculate real-world vertices
      const realWorldVertices = shape.realWorldVertices || 
        shape.vertices.map(v => ({ x: v.x / scale, y: v.y / scale }));
      
      // Calculate new pixel coordinates
      const newVertices = realWorldVertices.map(v => ({
        x: v.x * scale * widthRatio,
        y: v.y * scale * heightRatio
      }));
      
      return {
        ...shape,
        vertices: newVertices,
        // Store real-world coordinates for future resizes
        realWorldVertices
      };
    }));
    
    // Adjust entry points: store and maintain real-world positions
    setEntryPoints(prevEntryPoints => prevEntryPoints.map(entry => {
      // Get or calculate real-world position
      const realWorldX = entry.realWorldX || entry.position.x / scale;
      const realWorldY = entry.realWorldY || entry.position.y / scale;
      
      return {
        ...entry,
        position: {
          x: realWorldX * scale * widthRatio,
          y: realWorldY * scale * heightRatio
        },
        // Store real-world coordinates
        realWorldX,
        realWorldY
      };
    }));
    
    console.log('Box dimensions recalculated successfully');
  }, [setBoxes, setShapes, setEntryPoints]);

  return {
    // State
    boxes,
    shapes,
    entryPoints,
    selectedSpace,
    selectedSpaces,
    isMultiSelecting,
    typeVisibility,
    nextId,
    
    // Setters
    setBoxes,
    setShapes,
    setEntryPoints,
    setSelectedSpace,
    setSelectedSpaces,
    setIsMultiSelecting,
    
    // Functions
    deleteSpace,
    clearAllSpaces,
    toggleSpaceType,
    updateSpaceCount,
    toggleSpaceVisibility,
    toggleTypeVisibility,
    toggleSelectedSpacesVisibility,
    handleCommandClick,
    handleSingleClick,
    calculateTotalArea,
    calculateSensorCounts,
    formatArea,
    addBox,
    addShape,
    addEntryPoint,
    
    // Undo functionality
    handleUndo,
    deleteSelectedSpaces,
    
    // For Waffle type management (meeting or desk)
    setWaffleType: useCallback((spaceType, spaceId, waffleType) => {
      if (spaceType === 'box') {
        setBoxes(prevBoxes => prevBoxes.map(box => {
          if (box.id === spaceId && box.type === SENSOR_TYPES.WAFFLE) {
            return { ...box, waffleType };
          }
          return box;
        }));
      } else if (spaceType === 'shape') {
        setShapes(prevShapes => prevShapes.map(shape => {
          if (shape.id === spaceId && shape.type === SENSOR_TYPES.WAFFLE) {
            return { ...shape, waffleType };
          }
          return shape;
        }));
      }
    }, [boxes, shapes]),

    // New functions
    updateSpaceAttribute,
    renumberSpaces,
    recalculateAllAreas,
    recalculateBoxDimensions
  };
}; 